import { useEffect, useMemo, useState } from "react"
import { Capacitor } from "@capacitor/core"
import { bearerFetcher, useAuthBearerFetch } from "matsuri-hooks"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { useTTLockKeyNumber } from "./useTTLockKeyNumber"
import { TTLock, TTLockUnlockResult } from "@/plugins/ttlock"
import { m2mCleaning_cleanerV4 } from "@/endpoints/m2m-cleaning.cleaner-v4"
import { useTranslation } from "@/hooks/useTranslation"

export const keyNumberAsNothing = "------"

export const useTTLockKeyUnlock = ({
    cleaningId,
    listingId
}: {
    cleaningId?: string
    listingId?: string
}) => {
    const { token } = useAuth()
    const availability = useTTLockAvailability({ listingId })
    const keyNumber = useTTLockKeyNumber({ listingId })
    const availableTTLock = useMemo(() => {
        return !!availability.data?.lockId
    }, [availability])
    const [progressUnlocking, setProgressUnlocking] = useState(false)
    const [unlockResult, setUnlockResult] = useState<TTLockUnlockResult>()

    useEffect(() => {
        // Androidの場合は TTLock SDK を初期化する必要がある
        if (Capacitor.getPlatform() === "android") {
            const initialize = async () => {
                if (!availableTTLock) {
                    return
                }
                // Initialize TTLock SDK
                await TTLock.initialize()
            }
            initialize()
        }
    }, [availableTTLock])

    const unlock = async () => {
        if (progressUnlocking || !cleaningId) {
            return
        }
        setProgressUnlocking(true)
        try {
            const { data: ekey, error } = await getOrCreateTTlockEKey(
                token,
                cleaningId
            )
            if (error) {
                throw error
            }
            if (!ekey?.ekey) {
                throw new Error("No ekey found")
            }

            const result = await TTLock.unlock({
                lockData: ekey.ekey
            })

            if (
                result.errorCode &&
                !shouldNotConsoleErrorTTLockErrorCode.includes(result.errorCode)
            ) {
                console.error({
                    message: `TTLock unlock sdk error: ${result.errorCode}`,
                    listingId,
                    cleaningId,
                    ekey
                })
            }
            setUnlockResult(result)
        } catch (e) {
            console.error({
                message: "TTLock unlock unhandled error",
                listingId,
                cleaningId,
                error: e
            })
            setUnlockResult({
                success: false,
                errorMessage: String(e)
            })
        } finally {
            setProgressUnlocking(false)
        }
    }

    const { t } = useTranslation()
    const formattedErrorMessage = useMemo(() => {
        if (!unlockResult || unlockResult?.success) {
            return undefined
        }
        return formatErrorMessage(unlockResult, t)
    }, [unlockResult, t])

    return {
        availableTTLock,
        unlock,
        progressUnlocking,
        unlockResult: {
            ...unlockResult,
            formattedErrorMessage
        },
        keyNumber: keyNumber?.data?.keyNumber ?? keyNumberAsNothing
    }
}

interface UseGetOrCreateTTlockEKeyOutput {
    lockId: string
    ekey: string
}

const getOrCreateTTlockEKey = (token: string, cleaningId: string) => {
    const endpoint = m2mCleaning_cleanerV4.getTTLockEKeyForCleaner

    return bearerFetcher<UseGetOrCreateTTlockEKeyOutput>(
        token,
        `${endpoint()}?cleaningId=${cleaningId}`,
        {
            method: endpoint.method
        }
    )
}

// https://github.com/ttlock/iOS_SDK_Demo/blob/master/TTLockFrameworks/TTLock.xcframework/ios-arm64/TTLock.framework/Headers/TTMacros.h#L65
const shouldNotConsoleErrorTTLockErrorCode = [
    "0x61",
    "0x62",
    "0x63",
    "0x64",
    "0x0a",
    "0x405",
    "0x400",
    "0x401"
]

const formatErrorMessage = (
    result: TTLockUnlockResult,
    t: (text: string) => string
) => {
    if (result.errorCode !== undefined) {
        // https://github.com/ttlock/iOS_SDK_Demo/blob/master/TTLockFrameworks/TTLock.xcframework/ios-arm64/TTLock.framework/Headers/TTMacros.h#L65
        switch (result.errorCode) {
            case "0x61": // ios
            case "0x405": // android
                return t("デバイスのBluetoothをオンにしてください。")
            case "0x62": // ios
            case "0x400": // android
                return t(
                    "TTLockが見つかりませんでした。TTLockの近くに移動し、稼働しているか確認してください。"
                )
            case "0x63":
                return t(
                    "TTLockとの接続が切れました。TTLockが近くにあるか確認してください。"
                )
            case "0x64": // ios
            case "0x401": // android
                return t(
                    "TTLockが処理中です。少し待ってから再度お試しください。"
                )
            case "0x0a": // 共通
                return t(
                    "TTLockの電池が切れています。TTLockの電池を交換してください。"
                )
        }
    }

    return `${t("エラーが発生しました。再度お試しいただくかパスコードで解錠してください。")}\n
    ${result.errorCode ? `${t("エラーコード")}: ${result.errorCode} ` : ""}
    ${result.errorMessage}`
}

interface UseTTLockAvailabilityOutput {
    lockId: string | null
}

const useTTLockAvailability = ({ listingId }: { listingId?: string }) => {
    const { token } = useAuth()
    const endpoint = m2mCleaning_cleanerV4.getTTLockByListingId
    const result = useAuthBearerFetch<UseTTLockAvailabilityOutput>(
        token,
        listingId ? endpoint({ listingId }) : null,
        {
            method: endpoint.method,
            swrConfig: {
                shouldRetryOnError: false
            }
        }
    )

    return result
}
